import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { CHAPTERS } from "../data";
import { Reveal, LineReveal, Stagger, StaggerItem } from "../lib/motion";

const MountainMotif = () => (
  <svg className="absolute right-0 bottom-0 w-[45%] opacity-[0.09] pointer-events-none" viewBox="0 0 400 300" fill="none">
    <path d="M0 300 L120 90 L170 150 L230 40 L400 300 Z" stroke="#c9a24a" strokeWidth="1.5" fill="none" />
    <path d="M60 300 L180 130 L250 200 L400 60" stroke="#c9a24a" strokeWidth="1" fill="none" />
    <path d="M0 260 C120 240 260 280 400 250" stroke="#c9a24a" strokeWidth="1" fill="none" />
  </svg>
);

const ChapterImage = ({ src, flip }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.12, 1]);

  return (
    <div ref={ref} className={`relative h-[46vh] lg:h-[78vh] overflow-hidden ${flip ? "swoop-top" : "swoop-bottom"}`}>
      <motion.img
        src={src}
        alt=""
        style={{ y, scale }}
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/40 to-transparent" />
      <div className={`absolute inset-x-0 ${flip ? "top-0" : "bottom-0"} h-[3px] bg-gradient-to-r from-transparent via-gold to-transparent`} />
    </div>
  );
};

const Chapter = ({ ch, index }) => {
  const flip = index % 2 === 1;
  return (
    <div className="relative grid lg:grid-cols-2 items-stretch">
      <div className={`relative ${flip ? "lg:order-2" : ""}`}>
        <ChapterImage src={ch.image} flip={flip} />
      </div>

      <div className={`relative px-6 md:px-12 lg:px-16 py-14 lg:py-24 flex flex-col justify-center ${flip ? "lg:order-1" : ""}`}>
        <MountainMotif />
        <div className="relative z-10 max-w-xl">
          <Reveal>
            <div className="flex items-center gap-4 mb-5">
              <span className="font-display italic text-gold text-2xl md:text-3xl">{ch.num}</span>
              <span className="h-px flex-1 bg-gold/40" />
              <span className="font-display italic text-cream/40 text-xl">/ 04</span>
            </div>
            <p className="text-gold text-xs uppercase tracking-luxe mb-4">{ch.eyebrow}</p>
          </Reveal>

          <h3 className="font-display uppercase text-cream text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight mb-7">
            <LineReveal lines={ch.title.split(" & ").map((t, i, a) => (i < a.length - 1 ? t + " &" : t))} stagger={0.1} />
          </h3>

          <Stagger className="flex flex-wrap gap-x-3 gap-y-2 mb-9">
            {ch.examples.map((ex) => (
              <StaggerItem key={ex}>
                <span className="text-cream/90 text-sm md:text-base font-light">
                  {ex}<span className="text-gold ml-3">•</span>
                </span>
              </StaggerItem>
            ))}
          </Stagger>

          <Reveal delay={0.1}>
            <p className="text-cream/70 font-light leading-relaxed mb-8">{ch.body}</p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="border-l-2 border-gold pl-6 py-1 mb-9">
              <p className="text-gold text-xs uppercase tracking-luxe mb-3">Why It Matters</p>
              <p className="text-cream/85 font-light leading-relaxed">{ch.matters}</p>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <button
              data-testid={`chapter-cta-${ch.id}`}
              onClick={() => document.querySelector("#contact").scrollIntoView({ behavior: "smooth" })}
              className="group inline-flex items-center gap-2 border border-gold/60 text-gold text-xs uppercase tracking-[0.18em] px-7 py-3.5 rounded-full hover:bg-gold hover:text-navy-deep transition-colors duration-300"
            >
              Let's Advance Your Story
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </Reveal>
        </div>
      </div>
    </div>
  );
};

export const Chapters = () => (
  <section id="chapters" className="relative bg-navy-deep grain">
    <div className="text-center pt-24 pb-6 px-6">
      <Reveal>
        <p className="text-gold text-xs uppercase tracking-luxe mb-4">The Kingdom We Serve</p>
        <h2 className="font-display uppercase text-cream text-4xl md:text-6xl tracking-tight">
          Four Callings. One Mission.
        </h2>
      </Reveal>
    </div>
    <div className="divide-y divide-gold/15">
      {CHAPTERS.map((ch, i) => (
        <Chapter key={ch.id} ch={ch} index={i} />
      ))}
    </div>
  </section>
);
